<template>
    <div v-if="visible" class="custom-popup">
        <div
            ref="popup"
            class="overflow-hidden px-[16px] pb-[16px] bg-white ai-popup-shadow flex flex-col gap-3"
            :style="{
                'min-width': minWidth,
                'width': width,
                'max-width': maxWidth,
                'min-height': minHeight,
                'height': height,
                'max-height': maxHeight
            }"
        >
            <div ref="dragHandle" class="flex justify-between cursor-move pt-[16px]">
                <div class="flex gap-1">
                    <p class="text-2xl select-none">{{ title }}</p>
                    <img v-if="titleImage" class="mb-2 w-[15px]" :src="titleImage" />
                </div>
                <div class="flex gap-1 cursor-default">
                    <dx-button
                        v-for="(item, index) in topButtons"
                        :key="index"
                        class="ai-button-base"
                        @click="item.click"
                    >
                        <component :is="item.svg" />
                    </dx-button>
                    <dx-button class="ai-button-base" @click="closePopup">
                        <ai-close-cross />
                    </dx-button>
                </div>
            </div>
            <div class="grow overflow-hidden overflow-y-auto">
                <slot></slot>
            </div>
            <div v-if="bottomButtonsEnabled" class="flex justify-end">
                <div class="flex gap-3 custom-user-popup-buttons">
                    <ai-button
                        v-if="firstButtonEnabled"
                        :text="firstButtonText"
                        stylingMode="blue"
                        :disabled="firstButtonDisabled"
                        @click="submitClick"
                    />
                    <ai-button
                        v-if="secondButtonEnabled"
                        :text="secondButtonText"
                        stylingMode="light-blue"
                        :disabled="secondButtonDisabled"
                        @click="closePopup"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { DxButton } from 'devextreme-vue';
import AiButton from '@/components/ai-button/ai-button.vue';

const popup = ref();
const dragHandle = ref();

const props = defineProps({
    title: {
        type: String,
        default: '',
    },
    titleImage: {
        type: String,
        default: '',
    },
    isExplicitClose: {
        type: Boolean,
        default: true,
    },
    visible: {
        type: Boolean,
        default: false,
    },
    bottomButtonsEnabled: {
        type: Boolean,
        default: false,
    },
    firstButtonText: {
        type: String,
        default: 'Сохранить',
    },
    firstButtonDisabled: {
        type: Boolean,
        default: false,
    },
    firstButtonEnabled: {
        type: Boolean,
        default: true,
    },
    secondButtonText: {
        type: String,
        default: 'Отмена',
    },
    secondButtonDisabled: {
        type: Boolean,
        default: false,
    },
    secondButtonEnabled: {
        type: Boolean,
        default: true,
    },
    refreshButtonEnabled: {
        type: Boolean,
        default: false,
    },
    topButtons: {
        type: Array,
        default: () => [],
    },
    minWidth: {
        type: String,
        default: 'none',
    },
    width: {
        type: String,
        default: 'auto',
    },
    maxWidth: {
        type: String,
        default: '1300px',
    },
    minHeight: {
        type: String,
        default: 'none',
    },
    height: {
        type: String,
        default: 'auto',
    },
    maxHeight: {
        type: String,
        default: 'none',
    },
});

const emit = defineEmits(['submit-click', 'update:visible', 'close-popup']);

onMounted(() => {
    if (props.visible) setupDrag();
});

const submitClick = () => {
    emit('submit-click');
};

const closePopup = () => {
    emit('close-popup');
    if (props.isExplicitClose) {
        emit('update:visible', false);
    }
};

const setupDrag = () => {
    if (!popup.value || !dragHandle.value) return;

    const handle = dragHandle.value;
    const element = popup.value;

    handle.onmousedown = function(event) {
        if (event.target.closest('button')) return;

        const rect = element.getBoundingClientRect();
        const shiftX = event.clientX - rect.left;
        const shiftY = event.clientY - rect.top;

        element.style.position = 'absolute';

        function moveAt(pageX, pageY) {
            let newLeft = pageX - shiftX;
            let newTop = pageY - shiftY;

            const minLeft = 0;
            const minTop = 0;
            const maxLeft = window.innerWidth - rect.width;
            const maxTop = window.innerHeight - rect.height;

            newLeft = Math.max(minLeft, Math.min(newLeft, maxLeft));
            newTop = Math.max(minTop, Math.min(newTop, maxTop));

            element.style.left = newLeft + 'px';
            element.style.top = newTop + 'px';
        }

        moveAt(event.pageX, event.pageY);

        function onMouseMove(event) {
            moveAt(event.pageX, event.pageY);
        }

        document.addEventListener('mousemove', onMouseMove);

        function onMouseUp() {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            element.style.cursor = '';
        }

        document.addEventListener('mouseup', onMouseUp, { once: true });
    };

    handle.ondragstart = function() {
        return false;
    };
};
</script>

<style scoped>
.custom-popup {
    z-index: 1501;
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    background: #00000052;
    display: flex;
    justify-content: center;
    align-items: center;
}

.ai-popup-shadow {
    box-shadow: 0 11px 15px -7px rgba(0, 0, 0, .2), 0 24px 38px 3px rgba(0, 0, 0, .14), 0 9px 46px 8px rgba(0, 0, 0, .12);
    border-radius: 20px;
}
</style>
